import React, { FormEvent, useEffect, useState } from 'react'
import { Form } from 'react-router-dom';
import { Alert, Button, IconButton, InputLabel, TextField } from '@mui/material';
import NumberField from '../shared/numberField';

import { ICustomerOperations } from '../../types/customer';

import { AppConfig } from '../../config/appConfig';
import { CustomerService } from '../../services/customerService';
import { ErrorProvider } from '../../classes/errorProvider';

import '../../styles/customerOperationsForm.scss';

export interface ICustomerOperationsForm {
	id: string;
	operations: ICustomerOperations;
	onOperationsUpdated(operations: ICustomerOperations): void;
}

const CustomerOperationsForm: React.FunctionComponent<ICustomerOperationsForm> = ({ id, operations, onOperationsUpdated }) => {
	const [formFields, setFormFields] = useState<ICustomerOperations>(operations);
	const [isSaving, setIsSaving] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | undefined>();
	const [successMessage, setSuccessMessage] = useState<string | undefined>();

	useEffect(() => {
		if (operations) {
			setFormFields(operations);
			setErrorMessage(undefined);
		}
		else {
			setErrorMessage(`This customer (ID ${id}) is missing an operations record. Please contact support for assistance.`);
		}
	}, [operations])

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		clearSaveResult();
		setIsSaving(true);

		CustomerService.setCustomerOperationsByCustomerId(formFields)
			.then((response) => {
				console.log('CustomerService.setCustomerOperationsByCustomerId: SUCCESS', response);
				setSuccessMessage("Customer operations information has been saved.");
				onOperationsUpdated(response.data);
				setTimeout(() => {
					setSuccessMessage(undefined);
				}, AppConfig.editSuccessToastMs);
			})
			.catch((error) => {
				console.log('CustomerService.setCustomerOperationsByCustomerId: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
			})
			.finally(() => {
				setIsSaving(false);
				window.scrollTo(0, 0);
			});
	}

	const clearSaveResult = () => {
		setErrorMessage(undefined);
		setSuccessMessage(undefined);
	};

	const handleNumberboxFocus = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => {
		event.target.select();
	}

	return (
		<section className="hwi-customer-operations-form">
			{successMessage &&
				<Alert className="alert-success"
					severity="success"
					action={<IconButton onClick={() => { setSuccessMessage(undefined); }} size='small'>X</IconButton>}>
					{successMessage}
				</Alert>
			}
			{errorMessage &&
				<Alert className="alert-error"
					severity="error"
					action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}>
					{errorMessage}
				</Alert>
			}
			{operations &&
				<Form onSubmit={(event) => handleSubmit(event)}>
					<div className="fields">
						<div className="panel forms">
							<div className="title">Forms</div>
							<div className="content">
								<div className="row instructions">
									How many times per week should each of the following forms be completed?
								</div>
								<div className="row">
									<div className="col">
										<InputLabel>Staffing</InputLabel>
										<NumberField
											className="staffing-field"
											name="staffing"
											value={formFields.staffingFormWeeklyCount}
											min={0}
											onFocus={handleNumberboxFocus}
											onChange={(event) => { setFormFields({ ...formFields, ["staffingFormWeeklyCount"]: +event.target.value }) }}
										>
										</NumberField>
									</div>
								</div>
								<div className="row">
									<div className="col">
										<InputLabel>5S</InputLabel>
										<NumberField
											className="fives-weekly-count-field"
											name="fives-weekly-count"
											value={formFields.fivesWeeklyCount}
											min={0}
											onFocus={handleNumberboxFocus}
											onChange={(event) => { setFormFields({ ...formFields, ["fivesWeeklyCount"]: +event.target.value }) }}
										>
										</NumberField>
									</div>
								</div>
								<div className="row">
									<div className="col">
										<InputLabel>Haz Inspection</InputLabel>
										<NumberField
											className="haz-inspection-weekly-count-field"
											name="haz-inspection-weekly-count"
											value={formFields.hazInspectionWeeklyCount}
											min={0}
											onFocus={handleNumberboxFocus}
											onChange={(event) => { setFormFields({ ...formFields, ["hazInspectionWeeklyCount"]: +event.target.value }) }}
										>
										</NumberField>
									</div>

								</div>
								<div className="row">
									<div className="col">
										<InputLabel>Safety Toolbox</InputLabel>
										<NumberField
											className="safety-toolbox-weekly-count-field"
											name="safety-toolbox-weekly-count"
											value={formFields.safetyToolboxWeeklyCount}
											min={0}
											onFocus={handleNumberboxFocus}
											onChange={(event) => { setFormFields({ ...formFields, ["safetyToolboxWeeklyCount"]: +event.target.value }) }}
										>
										</NumberField>
									</div>
								</div>
								<div className="row">
									<div className="col count">
										<InputLabel>Plant Specific A</InputLabel>
										<NumberField
											className="form-one-weekly-count-field"
											name="form-one-weekly-count"
											value={formFields.plantSpecificFormOneWeeklyCount}
											min={0}
											onFocus={handleNumberboxFocus}
											onChange={(event) => { setFormFields({ ...formFields, ["plantSpecificFormOneWeeklyCount"]: +event.target.value }) }}
										>
										</NumberField>
									</div>
									<div className="col label">
										<InputLabel>Form Name</InputLabel>
										<TextField className="form-one-name-field" name="form-one-name" value={formFields.plantSpecificFormOneName} size="small" fullWidth placeholder="Optional" onChange={(event) => { setFormFields({ ...formFields, ["plantSpecificFormOneName"]: event.target.value }) }}></TextField>
									</div>
								</div>
								<div className="row">
									<div className="col count">
										<InputLabel>Plant Specific B</InputLabel>
										<NumberField
											className="form-two-weekly-count-field"
											name="form-two-weekly-count"
											value={formFields.plantSpecificFormTwoWeeklyCount}
											min={0}
											onFocus={handleNumberboxFocus}
											onChange={(event) => { setFormFields({ ...formFields, ["plantSpecificFormTwoWeeklyCount"]: +event.target.value }) }}
										>
										</NumberField>
									</div>
									<div className="col label">
										<InputLabel>Form Name</InputLabel>
										<TextField className="form-two-name-field" name="form-two-name" value={formFields.plantSpecificFormTwoName} size="small" fullWidth placeholder="Optional" onChange={(event) => { setFormFields({ ...formFields, ["plantSpecificFormTwoName"]: event.target.value }) }}></TextField>
									</div>
								</div>
							</div>
						</div>
						<div className="panel equipment">
							<div className="title">Equipment</div>
							<div className="content">
								<div className="row instructions">
									Please indicate the expected number of inspections per week for each quipment type.
								</div>
								<div className="row">
									<div className="col">
										<InputLabel>Balers</InputLabel>
										<NumberField
											className="balers-field"
											name="balers"
											value={formFields.balers}
											min={0}
											onFocus={handleNumberboxFocus}
											onChange={(event) => { setFormFields({ ...formFields, ["balers"]: +event.target.value }) }}
										>
										</NumberField>
									</div>
									<div className="col">
										<InputLabel>Side-By-Sides</InputLabel>
										<NumberField
											className="sidebysides-field"
											name="sidebysides"
											value={formFields.sideBySides}
											min={0}
											onFocus={handleNumberboxFocus}
											onChange={(event) => { setFormFields({ ...formFields, ["sideBySides"]: +event.target.value }) }}
										>
										</NumberField>
									</div>
								</div>
								<div className="row">
									<div className="col">
										<InputLabel>Compactors</InputLabel>
										<NumberField
											className="compactors-field"
											name="compactors"
											value={formFields.compactors}
											min={0}
											onFocus={handleNumberboxFocus}
											onChange={(event) => { setFormFields({ ...formFields, ["compactors"]: +event.target.value }) }}
										>
										</NumberField>
									</div>
									<div className="col">
										<InputLabel>Skidsteers</InputLabel>
										<NumberField
											className="skidsteers-field"
											name="skidsteers"
											value={formFields.skidsteers}
											min={0}
											onFocus={handleNumberboxFocus}
											onChange={(event) => { setFormFields({ ...formFields, ["skidsteers"]: +event.target.value }) }}
										>
										</NumberField>
									</div>
								</div>
								<div className="row">
									<div className="col">
										<InputLabel>Docks</InputLabel>
										<NumberField
											className="docks-field"
											name="docks"
											value={formFields.docks}
											min={0}
											onFocus={handleNumberboxFocus}
											onChange={(event) => { setFormFields({ ...formFields, ["docks"]: +event.target.value }) }}
										>
										</NumberField>
									</div>
									<div className="col">
										<InputLabel>Tractors</InputLabel>
										<NumberField
											className="tractors-field"
											name="tractors"
											value={formFields.tractors}
											min={0}
											onFocus={handleNumberboxFocus}
											onChange={(event) => { setFormFields({ ...formFields, ["tractors"]: +event.target.value }) }}
										>
										</NumberField>
									</div>
								</div>
								<div className="row">
									<div className="col">
										<InputLabel>Forklifts</InputLabel>
										<NumberField
											className="forklifts-field"
											name="forklifts"
											value={formFields.forklifts}
											min={0}
											onFocus={handleNumberboxFocus}
											onChange={(event) => { setFormFields({ ...formFields, ["forklifts"]: +event.target.value }) }}
										>
										</NumberField>
									</div>
									<div className="col">
										<InputLabel>Trucks</InputLabel>
										<NumberField
											className="trucks-field"
											name="trucks"
											value={formFields.trucks}
											min={0}
											onFocus={handleNumberboxFocus}
											onChange={(event) => { setFormFields({ ...formFields, ["trucks"]: +event.target.value }) }}
										>
										</NumberField>
									</div>
								</div>
								<div className="row">
									<div className="col">
										<InputLabel>Golf Carts</InputLabel>
										<NumberField
											className="golf-carts-field"
											name="golf-carts"
											value={formFields.golfCarts}
											min={0}
											onFocus={handleNumberboxFocus}
											onChange={(event) => { setFormFields({ ...formFields, ["golfCarts"]: +event.target.value }) }}
										>
										</NumberField>
									</div>
									<div className="col">
										<InputLabel>Walkies</InputLabel>
										<NumberField
											className="walkies-field"
											name="walkies"
											value={formFields.walkies}
											min={0}
											onFocus={handleNumberboxFocus}
											onChange={(event) => { setFormFields({ ...formFields, ["walkies"]: +event.target.value }) }}
										>
										</NumberField>
									</div>
								</div>
								<div className="row">
									<div className="col">
										<InputLabel>Ramps</InputLabel>
										<NumberField
											className="ramps-field"
											name="ramps"
											value={formFields.ramps}
											min={0}
											onFocus={handleNumberboxFocus}
											onChange={(event) => { setFormFields({ ...formFields, ["ramps"]: +event.target.value }) }}
										>
										</NumberField>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div className="controls">
						<Button type="submit" className="save-button" disabled={isSaving} variant="contained">{isSaving ? "Saving..." : "Save"}</Button>
					</div>
				</Form>
			}
		</section >
	)
}

export default CustomerOperationsForm;